import React__default from "react";
import debounce from "debounce";
import "tabbable";
import { usePrevious } from "../helpers/helper/hooks/use-previous.mjs";
import { Image } from "../image/image.mjs";
const ObjectFit = (props) => {
  let imageRef;
  const [browserSupport, setBrowserSupport] = React__default.useState(true);
  const [imageHeight, setImageHeight] = React__default.useState(0);
  const [imageWidth, setImageWidth] = React__default.useState(0);
  const [style, setStyle] = React__default.useState({});
  const prevWidth = usePrevious(imageWidth);
  const prevHeight = usePrevious(imageHeight);
  React__default.useEffect(() => {
    if (prevWidth !== imageWidth || prevHeight !== imageHeight) {
      setObjectFitStyles();
    }
  });
  React__default.useEffect(() => {
    if (!("objectFit" in document.body.style) && browserSupport) {
      setBrowserSupport(false);
    }
  }, [browserSupport]);
  const BEM = (className) => {
    const classArray = ["object-fit"];
    const singlePositionAxis = props.positionX && props.positionX || props.positionY && props.positionY || void 0;
    if (props.fit) {
      classArray.push("object-fit--".concat(props.fit));
    }
    if (props.positionX && props.positionY) {
      classArray.push("object-position--".concat(props.positionY, "-").concat(props.positionX));
    } else if (singlePositionAxis) {
      classArray.push("object-position--".concat(singlePositionAxis));
    }
    if (props.className) {
      classArray.push(props.className);
    }
    if (className) {
      classArray.push(className);
    }
    return classArray.join(" ");
  };
  const setContentStyleProperty = (property, value) => {
    const newStyle = {
      [property]: value
    };
    setStyle((prevState) => ({
      ...prevState,
      ...newStyle
    }));
  };
  const onLoad = (event) => {
    const target = event.target;
    if (target instanceof HTMLImageElement && imageHeight !== target.naturalHeight) {
      setImageHeight(target.naturalHeight);
    }
    if (target instanceof HTMLImageElement && imageWidth !== target.naturalWidth) {
      setImageWidth(target.naturalWidth);
    }
  };
  const setObjectPosition = (posX, posY) => {
    if (posX && posY) {
      return posX.concat(" " + posY);
    }
    return posX || posY;
  };
  const setObjectFitStyles = () => {
    if (browserSupport) {
      return;
    }
    const { fit, positionX, positionY } = props;
    const position = setObjectPosition(positionX, positionY);
    if (imageRef && imageRef.parentElement && !browserSupport) {
      const parentClientWidth = imageRef.parentElement.clientWidth;
      const parentClientHeight = imageRef.parentElement.clientHeight;
      const fitImageWidth = imageWidth / imageHeight * parentClientHeight;
      const fitImageHeight = imageHeight / imageWidth * parentClientWidth;
      setContentStyleProperty("position", "absolute");
      setContentStyleProperty("width", "auto");
      setContentStyleProperty("height", "auto");
      if (fit === "fill") {
        setContentStyleProperty("width", "100%");
        setContentStyleProperty("height", "100%");
        if (position) {
          setPosition(parentClientWidth, "x", position);
          setPosition(parentClientHeight, "y", position);
        }
        return;
      } else if (fit === "cover" && fitImageWidth >= parentClientWidth || fit === "contain" && fitImageWidth < parentClientWidth) {
        setContentStyleProperty("top", "0");
        setContentStyleProperty("marginTop", "0");
        setContentStyleProperty("maxWidth", "none");
        setContentStyleProperty("width", fitImageWidth + "px");
        setContentStyleProperty("height", parentClientHeight + "px");
        if (position) {
          setPosition(fitImageWidth, "x", position);
        }
      } else {
        setContentStyleProperty("width", "100%");
        setContentStyleProperty("height", "auto");
        setContentStyleProperty("left", "0");
        setContentStyleProperty("marginLeft", "0");
        if (position) {
          setPosition(fitImageHeight, "y", position);
        }
      }
    }
  };
  const debouncedResizeHandler = debounce(setObjectFitStyles, 50);
  React__default.useEffect(() => {
    window.addEventListener("resize", debouncedResizeHandler);
    return () => {
      window.removeEventListener("resize", debouncedResizeHandler);
      debouncedResizeHandler.clear();
    };
  }, [debouncedResizeHandler]);
  const setPosition = (imageSide, axis, objectPosition) => {
    let position;
    let other;
    let start;
    let end;
    let side;
    const objectPos = objectPosition.split(" ");
    if (objectPos.length < 2) {
      objectPos[1] = objectPos[0];
    }
    if (axis === "x") {
      position = objectPos[0];
      other = objectPos[1];
      start = "left";
      end = "right";
      side = imageSide;
    } else if (axis === "y") {
      position = objectPos[1];
      other = objectPos[0];
      start = "top";
      end = "bottom";
      side = imageSide;
    } else {
      return;
    }
    if (position === start || other === start) {
      setContentStyleProperty(start, "0");
      return;
    }
    if (position === end || other === end) {
      setContentStyleProperty(end, "0");
      return;
    }
    if (position === "center" || position === "50%" || other === "center" || other === "50%") {
      setContentStyleProperty(start, "50%");
      setContentStyleProperty("margin" + start.charAt(0).toUpperCase() + start.substring(1), side / -2 + "px");
      return;
    }
    if (position.indexOf("%") >= 0) {
      position = parseInt(position);
      if (position < 50) {
        setContentStyleProperty(end, position + "%");
        setContentStyleProperty("margin" + start.charAt(0).toUpperCase() + start.substring(1), position / -100 + "px");
      } else {
        position = 100 - position;
        setContentStyleProperty(end, position + "%");
        setContentStyleProperty("margin" + end.charAt(0).toUpperCase() + end.substring(1), position / -100 + "px");
      }
    } else {
      setContentStyleProperty(start, position);
    }
  };
  const mapChildren = () => {
    return React__default.Children.toArray(props.children).filter((child) => {
      return React__default.isValidElement(child) && child.type === Image;
    }).map((child) => {
      return React__default.cloneElement(child, {
        className: BEM(child.props.className),
        imageRef: (el) => imageRef = el,
        onLoad: (e) => onLoad(e),
        style: !browserSupport ? style : void 0
      });
    });
  };
  return /* @__PURE__ */ React__default.createElement(React__default.Fragment, null, mapChildren());
};
export {
  ObjectFit
};
